import { Stack, Toggle } from "@fluentui/react";
import { DataVizPalette, getColorFromToken, Legends, VerticalBarChart } from "@fluentui/react-charting";
import { IStandardChartData } from "models/IChartData";
import { useState } from "react";

export const CustomBarChart = ({ chartData }: { chartData: IStandardChartData }) => {
    const { x, series, x_label, y_label } = chartData;

    const [useLogScale, setUseLogScale] = useState(false);
    const [useNormalization, setUseNormalization] = useState(false);

    const uniqueCategories = Array.from(new Set(series.map((s) => s.name)));

    const categoryColors: Record<string, string> = {};
    uniqueCategories.forEach((category, index) => {
        categoryColors[category] = getColorFromToken(DataVizPalette[`color${(index % 20) + 1}` as keyof typeof DataVizPalette]);
    });

    const maxValue = Math.max(...series.flatMap((s) => s.y));
    const normalize = (value: number) => (value / maxValue) * 100;
    const SCALE_FACTOR = 10;
    const logTransform = (value: number) => Math.log10(value + 1) * SCALE_FACTOR;

    const barChartData = series.flatMap((s) =>
        x.map((year, index) => ({
            key: `${s.name}-${year}`,
            x: year,
            y: useLogScale ? logTransform(s.y[index]) : (useNormalization ? normalize(s.y[index]) : s.y[index]),
            legend: s.name,
            color: categoryColors[s.name],
            xAxisCalloutData: year.toString(),
            yAxisCalloutData: `${s.y[index].toLocaleString()} units`,
        }))
    );

    const uniqueLegends = uniqueCategories.map((name) => ({
        title: name,
        color: categoryColors[name],
    }));

    const allValues = barChartData.map(d => d.y);
    const yMinValue = useLogScale ? Math.min(...allValues) * 0.8 : 0;
    const yMaxValue = Math.max(...allValues) * 1.2;

    const barWidth = Math.max(15, 120 / uniqueCategories.length);
    const chartWidth = Math.max(900, x.length * uniqueCategories.length * 20);

    return (
        <Stack tokens={{ childrenGap: 20 }} style={{ maxWidth: "1000px", margin: "auto", textAlign: "center" }}>
            <Stack horizontal tokens={{ childrenGap: 20 }} style={{ justifyContent: "center" }}>
                <Toggle
                    label="Use Logarithmic Scale"
                    checked={useLogScale}
                    onChange={() => {
                        setUseLogScale(!useLogScale);
                        if (!useLogScale) setUseNormalization(false);
                    }}
                    inlineLabel
                />
                <Toggle
                    label="Use Normalization (0-100%)"
                    checked={useNormalization}
                    onChange={() => {
                        setUseNormalization(!useNormalization);
                        if (!useNormalization) {
                            setUseLogScale(false);
                        }
                    }}
                    inlineLabel
                />
            </Stack>

            <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                <Legends
                    legends={uniqueLegends}
                    overflowText="+ More"
                    allowFocusOnLegends={true}
                    canSelectMultipleLegends={false}
                    styles={{
                        root: {
                            fontSize: uniqueLegends.length > 10 ? "10px" : "12px",
                            textAlign: "center"
                        }
                    }}
                />
            </div>

            <VerticalBarChart
                culture={typeof window !== "undefined" ? window.navigator.language : "en-us"}
                data={barChartData}
                height={500}
                width={chartWidth}
                hideLegend={true}
                hideTooltip={false}
                showXAxisLablesTooltip={true}
                barWidth={barWidth}
                xAxisTitle={x_label}
                yAxisTitle={y_label}
                yMinValue={yMinValue}
                yMaxValue={yMaxValue}
                margins={{ left: 50, right: 30, bottom: 60, top: 20 }}
            />
        </Stack>
    );
};